import { ArrowRight, Building2, Headset, UserRound } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Link, Navigate } from 'react-router';
import { useAuth } from '@/lib/auth/AuthProvider';
import { IS_DEMO } from '@/lib/env';
import { AUDIENCE_HOME, AUDIENCE_LOGIN, type Audience } from '@/lib/auth/roles';
import { AuthLayout } from './AuthLayout';

// Demo build only: `IS_DEMO` is a build-time constant, so the live bundle never includes this chunk.
const DemoAccounts = IS_DEMO ? lazy(() => import('@/demo/DemoAccounts')) : null;

const options: { audience: Audience; title: string; text: string; icon: typeof Building2 }[] = [
  { audience: 'owner', title: 'Property owner or manager', text: 'Run your properties, residents, rent and team.', icon: Building2 },
  { audience: 'tenant', title: 'Resident', text: 'Pay rent, mark meals, raise issues and manage your stay.', icon: UserRound },
  { audience: 'internal', title: 'MOVO team', text: 'Platform console for MOVO staff.', icon: Headset },
];

export function LoginChooser() {
  const { session } = useAuth();
  if (session) {
    return <Navigate to={AUDIENCE_HOME[session.audience]} replace />;
  }
  return (
    <AuthLayout>
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Welcome to MOVO</h1>
      <p className="mt-2 text-slate-500">Choose how you’ll be signing in.</p>
      {DemoAccounts && (
        <div className="mt-6">
          <Suspense fallback={null}>
            <DemoAccounts />
          </Suspense>
        </div>
      )}
      <ul className="mt-8 space-y-3">
        {options.map(({ audience, title, text, icon: Icon }) => (
          <li key={audience}>
            <Link
              to={AUDIENCE_LOGIN[audience]}
              className="group flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-card transition-all hover:border-amethyst-300 hover:shadow-raised"
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-amethyst-50 text-amethyst-700">
                <Icon className="size-5" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-900">{title}</span>
                <span className="block text-sm text-slate-500">{text}</span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-amethyst-700" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </AuthLayout>
  );
}
